import React, { useContext, useRef } from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/core'
import ReCAPTCHA from 'react-google-recaptcha'
import { useTheme } from 'emotion-theming'
import { string, bool } from 'prop-types'
import Loadable from '@loadable/component'

import { handleResponse, isValidEmail, createCrossBrowserEvent, composeValidators, required } from '@jelly/utils'
import { Button } from '@jelly/ui'
import { Arrow } from '../svgs/Arrow'
import { ArrowWrapper } from '../ArrowBlock'
import { Warning } from '../svgs/Warning'
import { SiteConfigContext } from '../SiteConfigProvider'
import { EmailSubscribeContext } from '../EmailSubscribeProvider'

const Field = Loadable(() => import(/* webpackChunkName: 'rff' */ 'react-final-form').then(({ Field }) => Field))
const Form = Loadable(() => import(/* webpackChunkName: 'rff' */ 'react-final-form').then(({ Form }) => Form))

const ButtonBase = ({ theme }) => css`
	background: ${theme.emailCapture.buttonColor};
	&:disabled {
		background-color: ${theme.colors.gray[400]};
	}
`
const SubscribeButton = styled(Button)`
	${ButtonBase}
	margin-top: 10px;
	width: 100%;
`
const SubscribeButtonInline = styled(Button)`
	${ButtonBase}
	height: 46px;
	padding: 0;
	width: 46px;

	.desktopSubscribe {
		display: none;
	}

	&:hover {
		background: ${({ theme }) => theme.emailCapture.inlineButtonHover};
		border: ${({ theme }) => theme.emailCapture.inlineButtonHover};
	}

	${({ theme }) => theme.MQ.M} {
		margin: auto;
		margin-left: 5px;
	}

	${({ theme }) => theme.MQ.L} {
		padding: 15px 8px;
		width: 30%;
		.desktopSubscribe {
			display: block;
			margin: auto;
			width: 100%;
		}
		svg {
			display: none;
		}
	}
`

const FormContainer = styled.div`
	letter-spacing: 0.35px;
	${({ emailSubmitted }) => (emailSubmitted ? 'display: none;' : 'display:flex;')}
	overflow: hidden;
`

const EmailInput = styled.input`
	${({ theme }) => theme.fonts.primary};
	border: 2px solid ${({ shouldShowError, theme }) => (shouldShowError ? theme.colors.red : theme.colors.gray[300])};
	border-radius: 0;
	box-sizing: border-box;
	height: 46px;
	padding: 11px 10px;
	width: 100%;
	&::placeholder {
		color: ${({ theme }) => theme.colors.gray[300]};
	}
	&:focus {
		outline: 0;
	}

	${({ theme }) => theme.MQ.M} {
		margin-top: 6px;
	}
`
const InputLabel = styled.label`
	font-size: 16px;
	color: ${({ theme }) => theme.emailCapture.labelColor};
	min-height: 25px; /* to support font face differences */
	display: none;

	${({ hasInlineButtons, theme }) => (hasInlineButtons ? theme.MQ.M : theme.MQ.XL)} {
		display: flex;
	}

	${({ theme }) => theme.BMQ.ML} {
		font-size: 1.7vw;
	}

	${({ theme }) => theme.BMQ.LXL} {
		font-size: 1.5vw; /* do not change, inline buttons will be off centered */
	}
`
const HeaderText = styled.div`
	margin: 0;
	font-weight: 400;
	font-size: inherit;
	${({ theme }) => theme.MQ.XL} {
		text-align: center;
	}
`
const ErrorMessage = styled.div`
	color: ${({ theme }) => theme.colors.red};
	padding: ${({ hasInlineButtons }) => (hasInlineButtons ? '5px 2px;' : '5px 2px 0 2px')};
	margin-bottom: ${({ hasInlineButtons }) => (hasInlineButtons ? '10px;' : '0')};
	font-size: 16px;
	line-height: 15px;
	display: flex;
	width: 100%;
	font-size: 13px;
	background-color: ${({ hasInlineButtons, theme }) => (hasInlineButtons ? theme.colors.white : 'inherit')};
	margin-top: 2px;
`
const WarningSVGContainer = styled.div`
	padding: 0 10px 0 0;
`
const SuccessMessageContainer = styled.div`
	display: ${({ emailSubmitted }) => (emailSubmitted ? 'block' : 'none')};
	color: ${({ theme }) => theme.colors.darkGrey};
	margin: 15px 0 20px 0;
	padding: 15px 0 0 0;
	font-size: 20px;
	font-family: 'Poppins', sans-serif;
	border-top: 1px solid ${({ theme }) => theme.colors.white};
	${({ theme }) => theme.fonts.header};
	${({ theme }) => theme.MQ.XL} {
		padding: 0 10px 20px 10px;
		margin: 20px;
		font-size: 24px;
		border-top: none;
	}
`
const SuccessFloaterContainer = styled.div`
	padding: 15px 10px;
	display: ${({ emailSubmitted }) => (emailSubmitted ? 'flex' : 'none')};
	color: inherit;
	margin: auto;
	background-color: inherit;
	font-size: 16px;
	font-family: 'Poppins', sans-serif;
	position: relative;
	left: -20px;
	${({ theme }) => theme.BMQ.ML} {
		font-size: 22px;
	}
	${({ theme }) => theme.MQ.L} {
		font-size: 26px;
	}
`
const InputWithLabelFloater = ({ theme }) => css`
	width: 82%;
	margin-right: 5px;
	overflow: hidden;
	${theme.BMQ.MXL} {
		width: 80%;
	}
	${theme.MQ.XL} {
		width: 400px;
		margin-right: 0;
	}
`
const InputWithLabel = styled.div`
	${({ hasInlineButtons }) => (hasInlineButtons ? InputWithLabelFloater : '')}
`

const ReCAPTCHAPolicy = styled.div`
	font-size: 9px;
	width: 100%;
	line-height: 12px;
	margin-top: 8px;
	text-align: left;
	justify-content: flex-start;
	color: inherit;
`
const ReCAPTCHAHidden = styled(ReCAPTCHA)`
	visibility: hidden;
`
const PolicyLink = styled.a`
	color: ${({ theme }) => theme.emailCapture.policyLink};
	&:visited {
		color: ${({ theme }) => theme.emailCapture.policyLink};
	}
	&:hover {
		color: ${({ theme }) => theme.emailCapture.policyLinkHover};
	}
`
const PolicyLinkOnArticles = styled.a`
	color: ${({ theme }) => theme.colors.contentLink};
	&:hover {
		color: ${({ theme }) => theme.colors.purple};
	}
`
const StyledForm = styled.form`
	width: 100%;
	display: ${({ hasInlineButtons }) => (hasInlineButtons ? 'flex' : 'block')};
`
const GOOGLE_PRIVACY_LINK = 'https://policies.google.com/privacy'

const GOOGLE_TERMS_OF_SERVICE_LINK = 'https://policies.google.com/terms'

const onEmailSubmit = async (values, source, emailSignupRoute) => {
	try {
		const url = `${emailSignupRoute}?recaptchaToken=${values.recaptchaToken}&email=${encodeURIComponent(
			values.email
		)}&source=${source}`
		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
		})
		return handleResponse(response)
	} catch (err) {
		console.error(err)
	}
}
export const EmailCaptureForm = ({ hasInlineButtons = false, source }) => {
	const {
		siteConstants: { EMAIL_API: emailSignupRoute, RECAPTCHA_SITE_KEY },
	} = useContext(SiteConfigContext)

	const { emailSubscribeFormSubmitted, setEmailSubscribeFormSubmitted } = useContext(EmailSubscribeContext)

	const {
		colors: { red },
	} = useTheme()
	const recaptchaRef = useRef()

	const SuccessMessage = () => {
		if (!emailSubscribeFormSubmitted) {
			return null
		}
		return hasInlineButtons ? (
			<SuccessFloaterContainer emailSubmitted={emailSubscribeFormSubmitted}>
				<HeaderText>Thanks for subscribing. You'll hear from us soon!</HeaderText>
			</SuccessFloaterContainer>
		) : (
			<FormContainer hasInlineButtons={hasInlineButtons}>
				<SuccessMessageContainer emailSubmitted={emailSubscribeFormSubmitted}>
					<HeaderText>Thanks for subscribing.</HeaderText>
					<HeaderText>You'll hear from us soon!</HeaderText>
				</SuccessMessageContainer>
			</FormContainer>
		)
	}

	const triggerRecaptcha = (e) => {
		e.preventDefault()
		recaptchaRef.current.execute()
	}

	return (
		<>
			<SuccessMessage />
			<FormContainer emailSubmitted={emailSubscribeFormSubmitted} hasInlineButtons={hasInlineButtons}>
				<Form
					onSubmit={async (values) => {
						await onEmailSubmit(values, source, emailSignupRoute)
						sessionStorage.setItem('emailSubscriptionEnabled', true)
						setEmailSubscribeFormSubmitted(true)
					}}
					initialValues={{ email: '', recaptchaToken: '' }}
					mutators={{
						setRecaptchaField: (args, state, utils) => {
							utils.changeValue(state, 'recaptchaToken', () => args[0])
						},
					}}
					render={({ submitting, handleSubmit, form: { mutators }, invalid }) => (
						<StyledForm
							hasInlineButtons={hasInlineButtons}
							id={source}
							onSubmit={() => {
								if (invalid) {
									recaptchaRef.current.reset()
								}

								handleSubmit()
							}}
						>
							<Field name="email" validate={composeValidators(required, isValidEmail)}>
								{({ input, meta }) => {
									const shouldShowError = meta.touched && meta.error
									return (
										<>
											<InputWithLabel hasInlineButtons={hasInlineButtons}>
												<InputLabel hasInlineButtons={hasInlineButtons}>
													Enter your email address below.
												</InputLabel>
												<EmailInput
													placeholder="you@yourdomain.com"
													type="email"
													shouldShowError={shouldShowError}
													{...input}
												/>
												{shouldShowError ? (
													<ErrorMessage hasInlineButtons={hasInlineButtons}>
														<WarningSVGContainer>
															<Warning fill={red} />
														</WarningSVGContainer>
														{meta.error &&
															'Please double-check the email is entered correctly (e.g., name@domain.com).'}
													</ErrorMessage>
												) : hasInlineButtons && !shouldShowError ? (
													<ReCAPTCHAPolicy>
														This site is protected by reCAPTCHA. Google's
														<PolicyLink href={GOOGLE_PRIVACY_LINK}>
															{' Privacy Policy '}
														</PolicyLink>
														and
														<PolicyLink href={GOOGLE_TERMS_OF_SERVICE_LINK}>
															{' Terms of Service '}
														</PolicyLink>
														apply.
													</ReCAPTCHAPolicy>
												) : null}
											</InputWithLabel>
										</>
									)
								}}
							</Field>
							{hasInlineButtons ? (
								<SubscribeButtonInline
									onClick={triggerRecaptcha}
									disabled={submitting}
									fullWidth={true}
									type="submit"
								>
									<span className="desktopSubscribe">Subscribe</span>
									<ArrowWrapper>
										<Arrow width={20} height={20} />
									</ArrowWrapper>
								</SubscribeButtonInline>
							) : (
								<SubscribeButton
									onClick={triggerRecaptcha}
									disabled={submitting}
									fullWidth={true}
									type="submit"
								>
									Subscribe
								</SubscribeButton>
							)}

							{hasInlineButtons ? null : (
								<ReCAPTCHAPolicy>
									This site is protected by reCAPTCHA. Google's
									<PolicyLinkOnArticles href={GOOGLE_PRIVACY_LINK}>
										{' Privacy Policy '}
									</PolicyLinkOnArticles>
									and
									<PolicyLinkOnArticles href={GOOGLE_TERMS_OF_SERVICE_LINK}>
										{' Terms of Service '}
									</PolicyLinkOnArticles>
									apply.
								</ReCAPTCHAPolicy>
							)}
							<ReCAPTCHAHidden
								ref={recaptchaRef}
								onChange={(token) => {
									if (!token) {
										return
									}
									mutators.setRecaptchaField(token)
									const submitEvent = createCrossBrowserEvent('submit')
									document.getElementById(source).dispatchEvent(submitEvent)
								}}
								size="invisible"
								sitekey={RECAPTCHA_SITE_KEY}
							/>
						</StyledForm>
					)}
				/>
			</FormContainer>
		</>
	)
}

EmailCaptureForm.propTypes = {
	source: string.isRequired,
	hasInlineButtons: bool,
}
